import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/api/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { TrendingUp, Plus, Edit, Trash2, Sparkles, Check, X, Loader2 } from "lucide-react";
import { toast } from "sonner";

const eur = (n) => new Intl.NumberFormat("fi-FI", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(Number(n || 0));

export default function UpsellPortalDashboard() {
  const [offers, setOffers] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [o, a] = await Promise.all([
        api.get("/portal/upsell-offers"),
        api.get("/portal/upsell/analytics"),
      ]);
      setOffers(o.data || []);
      setAnalytics(a.data);
    } catch (e) { toast.error(formatApiError(e)); }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      const f = { ...form };
      f.final_price = (parseFloat(f.base_price) || 0) * (1 - (parseFloat(f.discount_percent) || 0) / 100);
      if (f.id) await api.put(`/portal/upsell-offers/${f.id}`, f);
      else await api.post("/portal/upsell-offers", f);
      toast.success("Tallennettu");
      setForm(null); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const setStatus = async (it, status) => {
    try { await api.put(`/portal/upsell-offers/${it.id}`, { ...it, status }); toast.success("Päivitetty"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Poistetaanko tarjous?")) return;
    try { await api.delete(`/portal/upsell-offers/${id}`); toast.success("Poistettu"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-6" data-testid="upsell-page">
      <header className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <p className="text-[10px] uppercase tracking-[0.3em] text-white/40">Upsell-portaali</p>
          <h1 className="text-3xl font-display gradient-text-luxe">Lisämyynnit & tarjoukset</h1>
          <p className="text-white/60 text-sm mt-1">Automaattiset upsell-ehdotukset asiakkaillesi.</p>
        </div>
        <Button onClick={() => setForm({ status: "draft", offer_type: "upgrade", discount_percent: 0 })} className="btn-luxe" data-testid="add-upsell">
          <Plus className="w-4 h-4 mr-1" /> Uusi tarjous
        </Button>
      </header>

      {analytics && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <Stat icon={<Sparkles className="w-4 h-4" />} label="Tarjouksia" value={analytics.total_offers} c="amber" />
          <Stat icon={<TrendingUp className="w-4 h-4" />} label="Konversio" value={`${analytics.conversion_rate}%`} c="emerald" />
          <Stat icon={<Check className="w-4 h-4" />} label="Hyväksytty" value={analytics.accepted} c="emerald" />
          <Stat icon={<X className="w-4 h-4" />} label="Hylätty" value={analytics.declined} c="zinc" />
          <Stat icon={<TrendingUp className="w-4 h-4" />} label="Liikevaihto" value={eur(analytics.revenue_generated)} c="violet" />
        </div>
      )}

      {loading && <Loader2 className="w-5 h-5 animate-spin text-white/40" />}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {offers.map((o) => (
          <div key={o.id} className="glass-card p-5" data-testid={`offer-${o.id}`}>
            <div className="flex items-start justify-between relative z-10">
              <div className="min-w-0">
                <h3 className="font-display text-white">{o.title}</h3>
                <p className="text-xs text-white/50">{o.customer_name || "—"}</p>
              </div>
              <Badge className={`text-[10px] border-0 ${
                o.status === "accepted" ? "bg-emerald-500/20 text-emerald-200" :
                o.status === "declined" ? "bg-red-500/20 text-red-200" :
                o.status === "sent" ? "bg-blue-500/20 text-blue-200" :
                "bg-amber-500/20 text-amber-200"
              }`}>
                {o.status === "accepted" ? "Hyväksytty" : o.status === "declined" ? "Hylätty" : o.status === "sent" ? "Lähetetty" : "Luonnos"}
              </Badge>
            </div>
            <div className="mt-3 relative z-10">
              <p className="text-xs text-white/40 line-through">{eur(o.base_price)}</p>
              <p className="text-2xl font-display text-amber-300">{eur(o.final_price)}</p>
              {o.discount_percent > 0 && <p className="text-xs text-emerald-400">-{o.discount_percent}% alennus</p>}
            </div>
            {o.description && <p className="text-xs text-white/60 mt-3 relative z-10 line-clamp-2">{o.description}</p>}
            <div className="flex gap-1 mt-4 relative z-10">
              {o.status === "draft" && <Button size="sm" onClick={() => setStatus(o, "sent")} className="btn-luxe flex-1 text-xs">Lähetä</Button>}
              {o.status === "sent" && <>
                <Button size="sm" onClick={() => setStatus(o, "accepted")} className="text-xs bg-emerald-600 hover:bg-emerald-500 flex-1">Hyväksy</Button>
                <Button size="sm" onClick={() => setStatus(o, "declined")} className="text-xs bg-red-600 hover:bg-red-500 flex-1">Hylkää</Button>
              </>}
              <Button size="sm" variant="ghost" onClick={() => setForm(o)}><Edit className="w-3 h-3" /></Button>
              <Button size="sm" variant="ghost" onClick={() => remove(o.id)} className="text-red-300"><Trash2 className="w-3 h-3" /></Button>
            </div>
          </div>
        ))}
        {offers.length === 0 && !loading && <p className="text-white/40">Ei tarjouksia vielä</p>}
      </div>

      <Dialog open={!!form} onOpenChange={(v) => !v && setForm(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>{form?.id ? "Muokkaa" : "Uusi upsell-tarjous"}</DialogTitle></DialogHeader>
          {form && (
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2"><Label>Otsikko *</Label><Input value={form.title || ""} onChange={(e) => setForm({ ...form, title: e.target.value })} /></div>
              <div><Label>Asiakkaan nimi</Label><Input value={form.customer_name || ""} onChange={(e) => setForm({ ...form, customer_name: e.target.value })} /></div>
              <div><Label>Tyyppi</Label>
                <select value={form.offer_type} onChange={(e) => setForm({ ...form, offer_type: e.target.value })} className="w-full bg-input/40 border border-white/10 rounded-lg px-3 py-2 text-sm">
                  <option value="upgrade">Päivitys</option><option value="addon">Lisäpalvelu</option><option value="renewal">Uusinta</option><option value="promotional">Tarjous</option>
                </select>
              </div>
              <div><Label>Perushinta €</Label><Input type="number" step="0.01" value={form.base_price || 0} onChange={(e) => setForm({ ...form, base_price: parseFloat(e.target.value) || 0 })} /></div>
              <div><Label>Alennus %</Label><Input type="number" value={form.discount_percent || 0} onChange={(e) => setForm({ ...form, discount_percent: parseFloat(e.target.value) || 0 })} /></div>
              <div><Label>Voimassa alkaen</Label><Input type="date" value={form.valid_from || ""} onChange={(e) => setForm({ ...form, valid_from: e.target.value })} /></div>
              <div><Label>Voimassa asti</Label><Input type="date" value={form.valid_until || ""} onChange={(e) => setForm({ ...form, valid_until: e.target.value })} /></div>
              <div className="col-span-2"><Label>Kuvaus</Label><Input value={form.description || ""} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
              <div className="col-span-2 flex gap-2 pt-2">
                <Button variant="ghost" onClick={() => setForm(null)} className="flex-1">Peruuta</Button>
                <Button onClick={save} className="btn-luxe flex-1">Tallenna</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Stat({ icon, label, value, c }) {
  const colors = { amber: "text-amber-300", emerald: "text-emerald-300", violet: "text-violet-300", zinc: "text-zinc-300" };
  return (
    <div className="stat-block">
      <div className={`w-9 h-9 rounded-lg bg-white/5 flex items-center justify-center ${colors[c]} mb-2`}>{icon}</div>
      <p className="text-[10px] uppercase tracking-widest text-white/40">{label}</p>
      <p className={`text-2xl font-display font-bold ${colors[c]}`}>{value}</p>
    </div>
  );
}
